import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Select, Form, Flex, message, Modal } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next";
import { resolveMasterFieldTranslation, resolveRowTranslation } from "../../../utils/masterTranslations.js";
import { buildGlobalMasterLabelMap, buildMasterOptionLabelMap, getLocalizedMasterCellText, isUuidLike, resolveMasterValueLabel } from "../../../utils/masterHelper";
import { getActiveSetValue, getAllWidgets, getConditionalRevertValue, shouldRevertConditionalValue, shouldSkipProfileAutofill } from "../../../utils/formsHelper";
import { getNameFromEmail } from "../../../utils/getNameFromEmail";
import { getSingleMasterData } from "../../../services/adminServices";
import { getPublicSingleMasterData } from "../../../services/publicFormServices";
import { getPreviousDataByValue, getPublicPreviousDataByValue } from "../../../services/requestServices";
import { showLoader } from "../../../utils/showloader";
import { setCachedMasterData, upsertMaster } from "../../../redux/slices/masterDataSlice";

const selectStyle = { width: "100%", textAlign: "left" };
const modalGridStyle = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", padding: "24px 0" };
const modalFieldStyle = { display: "flex", flexDirection: "column", gap: "8px" };
const modalLabelStyle = { fontWeight: 500, color: "#666", fontSize: "14px" };
const modalValueStyle = { padding: "8px 12px", border: "1px solid #d9d9d9", borderRadius: "4px", color: "#000", backgroundColor: "#fafafa", minHeight: "32px", display: "flex", alignItems: "center" };

const normalizeOptions = (rawOptions) => {
    if (!Array.isArray(rawOptions)) return [];
    return rawOptions
        .map((opt) => {
            if (typeof opt === "string") {
                const trimmed = opt.trim();
                if (!trimmed) return null;
                return { label: trimmed, value: trimmed };
            }

            if (!opt || typeof opt !== "object") return null;

            const optLabel = typeof opt.label === "string" ? opt.label : "";
            const fallbackValue = optLabel ? optLabel : opt.value;

            return {
                ...opt,
                label: optLabel,
                value: opt.value ?? fallbackValue,
            };
        })
        .filter((opt) => opt && opt.label);
};

// Loose comparison key for profile matching: case, surrounding space and the
// `.`/`_`/`-` separators email local parts use are all ignored, so
// "john.doe" and "John Doe" line up.
const normalizeForMatch = (value) => {
    if (value === undefined || value === null) return "";
    return String(value).toLowerCase().replace(/[._\-\s]+/g, " ").trim();
};

// Reads the master data autopopulate switch off a form element.
// The builder switch was renamed `autoPopulate` -> `autoPopulateMaster`. The new key
// wins whenever it is present, so switching the toggle off sticks even next to a
// stale legacy value; forms saved before the rename fall back to the old key.
const isAutoPopulated = (field) => (
    field?.autoPopulateMaster !== undefined ? !!field.autoPopulateMaster : !!field?.autoPopulate
);

const isFilledSelection = (value) => {
    if (value === undefined || value === null || value === "") return false
    if (Array.isArray(value)) return value.some(isFilledSelection)
    return true
}

const flattenSelections = (value) => (
    Array.isArray(value)
        ? value.filter((item) => item !== undefined && item !== null && item !== "")
        : (isFilledSelection(value) ? [value] : [])
)

const selectionsAreEqual = (a, b) => {
    if (Array.isArray(a) || Array.isArray(b)) {
        const left = Array.isArray(a) ? a : []
        const right = Array.isArray(b) ? b : []
        return left.length === right.length && left.every((item, index) => item === right[index])
    }
    return a === b
}

// Request forms slice `formValues` to this field's own keys. Master sibling
// filtering needs the other master dropdowns, so this watches the live store
// and only notifies when those sibling values actually change.
const MasterSiblingWatcher = ({ form, siblingIds, onSlice }) => {
    const allValues = Form.useWatch([], { form, preserve: true })
    const prevRef = useRef()

    useEffect(() => {
        const slice = {}
        siblingIds.forEach((sid) => {
            slice[sid] = allValues?.[sid] ?? form?.getFieldValue?.(sid)
        })
        const prev = prevRef.current
        if (prev && siblingIds.every((sid) => selectionsAreEqual(prev[sid], slice[sid]))) {
            return
        }
        prevRef.current = slice
        onSlice(slice)
    }, [allValues, form, siblingIds, onSlice])

    return null
}

// Module-level set to track in-flight fetches — shared across all instances synchronously
const pendingFetches = new Set();

const EMPTY_SELECTION = []

const isStandaloneMasterDropdown = (field) => (
    !!field?.parentTable
    && field?.formType === "dropdownField"
    && !String(field?.id ?? "").includes("::")
)

// Canonical master column a dropdown is bound to. Same-table fields that share
// this key are independent lookups (several Yes/No questions), not a cascade.
const getMasterColumnKey = (field) => String(
    field?.mappedColumn || field?.actualLabel || field?.label || ""
).trim().toLowerCase()

const DropdownInput = (props) => {
    const { spaceId, requestId } = useParams()
    const { id, label, placeholder, tooltip, helpText, formValues, mandatory, readOnly, options = [], sort, other, previewMode, previewmode, form, setValueData, mappedColumn="", multiSelect=false, spaceColor, isPublicForm=false, enablePreviousDataFetch, requestDetail, enableScoring=false, maxSelectableOptions, highlight=false, mapProfile=false, profileField, ...rest } = props
    const isPreview = previewMode || previewmode;
    const isEditing = !!(requestDetail && Object.keys(requestDetail).length > 0);
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { masterDataCache, masterList } = useSelector((state) => state.masterData);
    const { userInfo } = useSelector((state) => state.user);
    const skipProfileAutofill = shouldSkipProfileAutofill({
        requestDetail,
        defaultRequestDetail: rest.defaultRequestDetail,
        userEmail: userInfo?.email
    })
    // On an existing-request URL, wait until the saved record is in hand so a
    // viewer is never mapped before we know this is someone else's form.
    const routeRequestId = requestId || props.requestId
    const requestContextReady = !routeRequestId || routeRequestId === "new"
        || !!(requestDetail?.requestId || rest.defaultRequestDetail?.requestId)
    // Public form links pin the language with `lng`; the widget `label` is already
    // localised by Wrapper, so the master column must be read from the canonical
    // form element (mappedColumn / original label), not from that translated label.
    const contentLang = rest.lng || i18n.language

    const rules = mandatory ? [{ required: true, message: "Please enter " + label }] : undefined;
    const disabled = previewMode && readOnly;
    const [optionsList, setOptionsList] = useState([])
    const [infoModalOpen, setInfoModalOpen] = useState(false)
    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [masterSiblingValues, setMasterSiblingValues] = useState({})
    const suppressNextOpenRef = useRef(false)
    const fieldLocked = disabled || rest.disableAllFields
    const help = tooltip ? { title: helpText, icon: <QuestionCircleOutlined /> } : undefined;

    const formFields = useMemo(() => {
        const byId = new Map()
        getAllWidgets(props.formElements || []).forEach((field) => {
            if (field?.id != null) byId.set(field.id, field)
        })
        getAllWidgets(props.allFormElements || []).forEach((field) => {
            if (field?.id != null && !byId.has(field.id)) byId.set(field.id, field)
        })
        return Array.from(byId.values())
    }, [props.formElements, props.allFormElements]);

    // This field's own definition, read the same way its siblings are. Falls back to
    // the props it was rendered with when the form element tree was not passed down.
    const selfElement = useMemo(
        () => formFields?.find((field) => field.id === id) ?? { autoPopulateMaster: props.autoPopulateMaster, autoPopulate: props.autoPopulate, mappedColumn, label },
        [formFields, id, props.autoPopulateMaster, props.autoPopulate, mappedColumn, label]
    )
    const canonicalColumn = mappedColumn || selfElement?.mappedColumn || selfElement?.label || label

    // Dependents only read master dropdowns that appear before them (Zone → State →
    // AOP State). Later fields must not constrain earlier ones, or a failed match on
    // AOP State would send State back to the full list.
    // Repeats of the same column stay out of this list: they are independent
    // lookups, and cascading them would leave every later field with only the
    // value picked in the first (Yes/No → only "No").
    const masterSiblingFields = useMemo(() => {
        if (!props.parentTable) return []
        const currentIndex = formFields.findIndex((field) => field.id === id)
        const currentColumn = getMasterColumnKey({ mappedColumn: canonicalColumn })
        return formFields.filter((field, index) => (
            isStandaloneMasterDropdown(field)
            && field.parentTable === props.parentTable
            && field.id !== id
            && (currentIndex < 0 || index < currentIndex)
            && (!currentColumn || getMasterColumnKey(field) !== currentColumn)
        ))
    }, [props.parentTable, formFields, id, canonicalColumn])

    const masterSiblingIds = useMemo(
        () => masterSiblingFields.map((field) => field.id),
        [masterSiblingFields]
    )

    const handleMasterSiblingSlice = useCallback((slice) => {
        setMasterSiblingValues(slice)
    }, [])

    const getTableData = useCallback(async () => {
        const fetchMaster = (tableId) => isPublicForm
            ? getPublicSingleMasterData(tableId, props.shareToken)
            : getSingleMasterData(tableId, undefined, { tenantId: rest.tenantId })

        // A column that itself points at another master stores that row's uuid.
        // Fetch the referenced table only when it is not already in masterList,
        // so a space that already loaded the full list is left untouched.
        const prefetchReferencedMasters = (master) => {
            const referencedIds = [...new Set((Array.isArray(master?.fields) ? master.fields : [])
                .map((field) => field?.parentTable)
                .filter((id) => id && id !== props.parentTable && !(Array.isArray(masterList) ? masterList : []).some((item) => item?.dataId === id)))]
            referencedIds.forEach((tableId) => {
                if (pendingFetches.has(tableId)) return
                pendingFetches.add(tableId)
                fetchMaster(tableId).then((parentResponse) => {
                    if (parentResponse?.data?.dataId) {
                        dispatch(upsertMaster(parentResponse.data))
                    }
                }).finally(() => {
                    pendingFetches.delete(tableId)
                })
            })
        }

        // Already fetched — use cached data
        if (Array.isArray(masterDataCache[props.parentTable])) {
            setOptionsList(masterDataCache[props.parentTable]);
            const cachedMaster = (Array.isArray(masterList) ? masterList : []).find((item) => item?.dataId === props.parentTable)
            prefetchReferencedMasters(cachedMaster)
            return;
        }

        // Another instance is already fetching — skip duplicate call
        if (pendingFetches.has(props.parentTable)) return;

        // Mark as in-flight synchronously so parallel mounts see it immediately
        pendingFetches.add(props.parentTable);

        // A public visitor has no auth token, so /master-data would 401 - read
        // the table through the form's share token instead.
        if (!isPublicForm) showLoader(true)
        let response = await fetchMaster(props.parentTable)
        if (!isPublicForm) showLoader(false)

        pendingFetches.delete(props.parentTable);

        if (response.data) {
            const master = response.data
            const data = master?.data;
            setOptionsList(data)
            dispatch(setCachedMasterData({ tableName: props.parentTable, data }));
            if (master?.dataId && !(Array.isArray(masterList) ? masterList : []).some((item) => item?.dataId === master.dataId)) {
                dispatch(upsertMaster(master))
            }
            prefetchReferencedMasters(master)
        } else {
            setOptionsList([])
            message.error(response.message)
        }
    }, [props.parentTable, masterDataCache, masterList, dispatch, isPublicForm, props.shareToken, rest.tenantId]);

    // #3: Merged two useEffects into one
    useEffect(() => {
        if (props.fieldId || props.parentTable) {
            getTableData();
        }
    }, [props.fieldId, props.parentTable, getTableData])

    // Sync optionsList when another instance's fetch populates the cache
    useEffect(() => {
        const cached = masterDataCache[props.parentTable];
        if (Array.isArray(cached) && cached.length > 0) {
            setOptionsList(cached);
        }
    }, [masterDataCache, props.parentTable])

    // Only show info modal on the first field from this master data group
    const isFirstInGroup = useMemo(() => {
        if (!props.parentTable) return false;
        const firstSibling = formFields?.find((field) => (
            isStandaloneMasterDropdown(field) && field.parentTable === props.parentTable
        ))
        return firstSibling?.id === id
    }, [props.parentTable, formFields, id])

    // Resolve a field's raw stored value to a display value. Fields that are themselves
    // a reference to another master store the referenced row's uuid, not display text.
    const resolveFieldDisplayValue = useCallback((fieldDef, rawValue) => {
        if (rawValue === undefined || rawValue === null || rawValue === "") return rawValue
        if (fieldDef?.parentTable) {
            const refMaster = masterList?.find(m => m.dataId === fieldDef.parentTable)
            const refRow = refMaster?.data?.find(r => r.uuid === rawValue)
            if (refRow) {
                const localizedRow = resolveRowTranslation(refRow, refMaster?.fields, contentLang)
                return localizedRow?.[fieldDef.mappedColumn || fieldDef.label] ?? rawValue
            }
            const localizedField = resolveMasterFieldTranslation(fieldDef, contentLang)
            const matchedOption = localizedField?.options?.find(opt => opt.value === rawValue)
            if (matchedOption) return matchedOption.label
        }
        return rawValue
    }, [masterList, contentLang])

    // #6: Store extraFields alongside selectedRowInfo so Modal doesn't recompute
    const { selectedRowInfo, infoFields } = useMemo(() => {
        if (!isFirstInGroup || !props.parentTable || !formValues?.[id] || !optionsList?.length)
            return { selectedRowInfo: null, infoFields: [] }
        const row = optionsList.find(row => row[canonicalColumn] === formValues[id] || row["uuid"] === formValues[id])
        if (!row) return { selectedRowInfo: null, infoFields: [] }
        const masterDef = masterList?.find(m => m.dataId === props.parentTable)
        const masterFieldDefs = masterDef?.fields || []
        const extraFields = masterFieldDefs.filter(f => f.label !== canonicalColumn && f.label !== mappedColumn)
        if (extraFields.length === 0) return { selectedRowInfo: null, infoFields: [] }
        const fieldDefs = masterFieldDefs.length
            ? masterFieldDefs
            : Object.keys(row).filter(k => !k.startsWith('_') && k !== 'id' && k !== 'uuid' && k !== 'translations').map(k => ({ label: k }))
        // Read from the localised row but key off the canonical label, which is
        // the property the value is actually stored under
        const localizedRow = resolveRowTranslation(row, masterFieldDefs, contentLang)
        const fields = fieldDefs.map(fieldDef => ({
            key: resolveMasterFieldTranslation(fieldDef, contentLang)?.label || fieldDef.label,
            value: resolveFieldDisplayValue(fieldDef, localizedRow[fieldDef.label])
        }))
        return { selectedRowInfo: row, infoFields: fields }
    }, [isFirstInGroup, props.parentTable, formValues, id, canonicalColumn, optionsList, masterList, mappedColumn, resolveFieldDisplayValue, contentLang])

    // A master backed option carries the referenced row's uuid as its `value` and a
    // either that row's uuid or a text snapshot as its `label`. Relabelling it in the reader's
    // language leaves the stored value untouched, so a request submitted in one
    // language still resolves correctly in every other.
    // The mapped column can itself point at another master, in which case both the row and
    // the option label hold the referenced row's uuid rather than display text. These are the
    // same maps the master data table resolves with, so a reference chain never shows an id.
    const masterLabelMaps = useMemo(() => {
        const masterDef = masterList?.find(m => m.dataId === props.parentTable)
        const masterFields = masterDef?.fields || []
        const column = canonicalColumn
        const columnField = masterFields.find(field => field?.label === column)
        return {
            masterDef,
            masterFields,
            column,
            columnField,
            columnLabelMap: buildMasterOptionLabelMap(columnField, masterList, contentLang),
            globalLabelMap: buildGlobalMasterLabelMap(masterList, contentLang)
        }
    }, [props.parentTable, masterList, canonicalColumn, contentLang])

    const masterOptionText = useMemo(() => {
        const textByUuid = new Map()
        if (!props.parentTable) return textByUuid
        const { masterDef, column, columnField } = masterLabelMaps
        const sourceRows = (optionsList?.length ? optionsList : masterDef?.data) || []
        sourceRows.forEach(row => {
            if (!row?.uuid) return
            const text = getLocalizedMasterCellText(row, column, columnField, masterDef, masterList, contentLang)
            // An unresolved id carries no display value, so leave the option's own label in
            // place rather than relabelling it with the id
            if (typeof text === "string" && text !== "" && !isUuidLike(text)) {
                textByUuid.set(String(row.uuid), text)
                // A reference column is keyed by the row it points at, so that id reads
                // the same live text as the row carrying it.
                const rawCell = row?.[column]
                if (columnField?.parentTable && rawCell !== undefined && rawCell !== null && rawCell !== ""
                    && !textByUuid.has(String(rawCell))) {
                    textByUuid.set(String(rawCell), text)
                }
            }
        })
        return textByUuid
    }, [props.parentTable, optionsList, masterLabelMaps, masterList, contentLang])

    // Ids a row of this master is reachable by, for options baked before reference
    // columns were keyed by the row they point at. Lets a widget that was never
    // re-saved answer with the same id the rest of the form uses.
    const referencedIdByRowUuid = useMemo(() => {
        const byRowUuid = new Map()
        const { masterDef, column, columnField } = masterLabelMaps
        if (!props.parentTable || !columnField?.parentTable) return byRowUuid
        const sourceRows = (optionsList?.length ? optionsList : masterDef?.data) || []
        sourceRows.forEach(row => {
            const rawCell = row?.[column]
            if (!row?.uuid || rawCell === undefined || rawCell === null || rawCell === "") return
            byRowUuid.set(String(row.uuid), String(rawCell))
        })
        return byRowUuid
    }, [props.parentTable, optionsList, masterLabelMaps])

    // #1 & #2: Memoize all selectOptions computation (normalize, deduplicate, parent-filter, sort)
    const selectOptions = useMemo(() => {
        // The reader sees the options in the language the form is being read in, so the
        // translated list is what is rendered. A public link used to read the widget
        // snapshot instead, because a master-backed overlay can carry a row uuid where
        // the label belongs - that falls back per option now, leaving options that do
        // have translated text translated.
        const canonicalOptions = Array.isArray(selfElement?.options) ? selfElement.options : []
        const localizedOptions = Array.isArray(options) ? options : []
        let sourceOptions = localizedOptions
        if (isPublicForm && canonicalOptions.length) {
            if (!localizedOptions.length) {
                sourceOptions = canonicalOptions
            } else {
                const canonicalByValue = new Map(
                    canonicalOptions.map((opt) => [String(opt && typeof opt === "object" ? opt.value : opt), opt])
                )
                sourceOptions = localizedOptions.map((opt) => {
                    const optLabel = opt && typeof opt === "object" ? opt.label : opt
                    if (typeof optLabel === "string" && optLabel.trim() !== "" && !isUuidLike(optLabel)) {
                        return opt
                    }
                    const canonical = canonicalByValue.get(String(opt && typeof opt === "object" ? opt.value : opt))
                    const canonicalLabel = canonical && typeof canonical === "object" ? canonical.label : canonical
                    if (typeof canonicalLabel !== "string" || canonicalLabel === "") {
                        return opt
                    }
                    return (opt && typeof opt === "object") ? { ...opt, label: canonicalLabel } : canonical
                })
            }
        }
        let opts = normalizeOptions(sourceOptions).map((opt) => {
            // Options baked before reference columns were keyed by the row they point
            // at still hold the row they were read from. Answer with the referenced id
            // so this field and the referenced master's own dropdown speak the same
            // ids, and keep the old one so a value stored under it is still recognised.
            const referencedId = referencedIdByRowUuid.get(String(opt.value))
            if (!referencedId || referencedId === String(opt.value)) return opt
            const rowUuids = Array.isArray(opt.rowUuids) ? [...opt.rowUuids] : []
            if (!rowUuids.includes(String(opt.value))) rowUuids.push(String(opt.value))
            return { ...opt, value: referencedId, rowUuids }
        }).map((opt) => {
            let reqLabel = "";
            if (opt?.elementCode) {
                reqLabel = opt.elementCode + " ";
            }
            // Prefer the live row text when the snapshot still holds a referenced
            // row uuid (older widgets). A baked display label such as "NORTH 1"
            // is kept as-is so existing forms do not change.
            const fromRow = masterOptionText.get(String(opt.value))
            const resolvedOwn = isUuidLike(opt.label)
                ? resolveMasterValueLabel(opt.label, masterLabelMaps.columnLabelMap, masterLabelMaps.globalLabelMap)
                : opt.label
            const ownLabel = (typeof resolvedOwn === "string" && resolvedOwn !== "" && !isUuidLike(resolvedOwn))
                ? resolvedOwn
                : opt.label
            reqLabel += (fromRow && (!isPublicForm || isUuidLike(ownLabel))) ? fromRow : ownLabel;
            if (opt?.abbreviation) {
                reqLabel += " " + opt.abbreviation;
            }
            return { ...opt, label: reqLabel };
        });

        // Deduplicate by (value,label), keeping every id the dropped duplicates
        // stood for so a value stored under one of them still finds its option.
        const seen = new Map();
        opts = opts.filter((opt) => {
            const k = `${String(opt.value)}::${String(opt.label)}`;
            const kept = seen.get(k);
            if (kept) {
                const rowUuids = Array.isArray(opt.rowUuids) ? opt.rowUuids : [];
                if (rowUuids.length) {
                    kept.rowUuids = [...new Set([...(kept.rowUuids || []), ...rowUuids])];
                }
                return false;
            }
            seen.set(k, opt);
            return true;
        });

        if (sort === "ascending") {
            opts.sort((a, b) => a.label.localeCompare(b.label));
        } else if (sort === "descending") {
            opts.sort((a, b) => b.label.localeCompare(a.label));
        }
        if (other) {
            opts.push({label: t('Other'), value: "Other"})
        }

        return opts;
    }, [options, selfElement, sort, other, masterOptionText, masterLabelMaps, referencedIdByRowUuid, t, isPublicForm])

    const masterRows = useMemo(() => {
        if (!props.parentTable) return []
        if (Array.isArray(optionsList) && optionsList.length) return optionsList
        if (Array.isArray(masterDataCache?.[props.parentTable]) && masterDataCache[props.parentTable].length) {
            return masterDataCache[props.parentTable]
        }
        const masterDef = (Array.isArray(masterList) ? masterList : []).find((master) => master?.dataId === props.parentTable)
        return Array.isArray(masterDef?.data) ? masterDef.data : []
    }, [props.parentTable, optionsList, masterDataCache, masterList])

    const valuesForMasterFilter = useMemo(
        () => ({ ...formValues, ...masterSiblingValues }),
        [formValues, masterSiblingValues]
    )

    const filteredSelectOptions = selectOptions
    const masterFilterActive = false

    const displaySelectOptions = useMemo(() => {
        const currentValues = flattenSelections(valuesForMasterFilter?.[id] ?? formValues?.[id])
        if (!currentValues.length) return filteredSelectOptions
        const existing = new Set(filteredSelectOptions.map((opt) => String(opt?.value)))
        const extras = []
        currentValues.forEach((val) => {
            if (existing.has(String(val))) return
            // A value stored before this field answered with the referenced id. The
            // option that replaced it knows the ids it stands for, so the saved one is
            // offered as well and the answer still reads as the choice it was.
            const legacy = selectOptions.find((opt) => (
                Array.isArray(opt?.rowUuids) && opt.rowUuids.some((uuid) => String(uuid) === String(val))
            ))
            if (legacy) {
                extras.push({ ...legacy, value: val })
                existing.add(String(val))
                return
            }
            if (!fieldLocked) return
            const fromFull = selectOptions.find((opt) => String(opt?.value) === String(val))
            if (fromFull) {
                extras.push(fromFull)
                existing.add(String(val))
            }
        })
        return extras.length ? [...filteredSelectOptions, ...extras] : filteredSelectOptions
    }, [fieldLocked, filteredSelectOptions, valuesForMasterFilter, formValues, id, selectOptions])

    useEffect(() => {
        if (!props.parentTable || fieldLocked || !masterFilterActive || !form) return
        const current = form.getFieldValue(id)
        if (!isFilledSelection(current)) return
        const allowed = new Set(filteredSelectOptions.map((opt) => String(opt?.value)))
        if (multiSelect && Array.isArray(current)) {
            const next = current.filter((val) => allowed.has(String(val)))
            if (next.length !== current.length) {
                form.setFieldValue(id, next)
            }
            return
        }
        const singleValue = Array.isArray(current) ? current[0] : current
        if (!allowed.has(String(singleValue))) {
            form.setFieldValue(id, undefined)
        }
    }, [props.parentTable, fieldLocked, masterFilterActive, form, id, filteredSelectOptions, multiSelect])

    const activeSetValue = useMemo(
        () => getActiveSetValue(setValueData, true),
        [setValueData]
    )


    const lastAppliedConditionalRef = useRef(undefined)

    useEffect(() => {
        if (activeSetValue?.value) {
            const key = activeSetValue.value
            if (disabled) {
                const current = form?.getFieldValue(id)
                if (current !== key) {
                    form?.setFieldValue(id, key)
                }
            } else if (lastAppliedConditionalRef.current !== key) {
                form?.setFieldValue(id, key)
            }
            lastAppliedConditionalRef.current = key
        } else if (shouldRevertConditionalValue(activeSetValue, { currentValue: formValues?.[id] ?? form?.getFieldValue(id), appliedValue: lastAppliedConditionalRef.current })) {
            // `disabled` is deliberately left out: a read-only dropdown can also be
            // holding a value that came with the request rather than from a
            // condition, and only the condition's own selection may be taken back.
            form?.setFieldValue(id, getConditionalRevertValue(id, props.defaultFormValues))
            lastAppliedConditionalRef.current = undefined
        } else if (activeSetValue && !activeSetValue.value && disabled) {
            // The condition that populated this read-only field no longer passes,
            // so drop the value it set.
            const existingValue = formValues?.[id] ?? form?.getFieldValue(id)
            if (existingValue === undefined || existingValue === null || existingValue === "") {
                form?.setFieldValue(id, getConditionalRevertValue(id, props.defaultFormValues))
            }
            lastAppliedConditionalRef.current = undefined
        } else {
            lastAppliedConditionalRef.current = undefined
        }
    }, [activeSetValue, form, id, disabled, formValues?.[id]])

    useEffect(() => {
        if (formValues?.[id] === "Other" && !other) {
            form.resetFields([id])
        }
    }, [formValues?.[id], other, form, id])

    const fetchPreviousData = useCallback(async (value) => {
        const response = isPublicForm
            ? await getPublicPreviousDataByValue(id, value, props.requestId, props.spaceId, props.shareToken)
            : await getPreviousDataByValue(id, value, spaceId, requestId);
        const fetchedValues = response?.data?.value;
        if (response.status === 200 && fetchedValues && typeof fetchedValues === "object") {
            const fieldsToSet = { ...fetchedValues };
            delete fieldsToSet[id];
            if (Object.keys(fieldsToSet).length > 0) {
                form?.setFieldsValue(fieldsToSet);
            }
        }
    }, [form, id, spaceId, requestId, isPublicForm, props.requestId, props.spaceId, props.shareToken]);

    const handleDropChange = useCallback((selectedValue) => {
        if (enablePreviousDataFetch && isPreview && !isEditing && selectedValue !== undefined && selectedValue !== null && selectedValue !== "") {
            fetchPreviousData(selectedValue);
        }
        if (!props.parentTable || !optionsList?.length) return;

        // The switch decides whether a field takes part in master autofill at all, on
        // both sides. A field with it off is left out entirely: it is never filled
        // from a row, and selecting it never rewrites the fields that are — so a
        // standalone column like Role can sit next to Email and BU without
        // disturbing them.
        if (!isAutoPopulated(selfElement)) return;

        // Fill every sibling from the same master table that opted in. Without the
        // opt-in gate, two unrelated fields built on the same master overwrite each
        // other on every selection.
        const siblingElements = formFields?.filter((field) => field.parentTable === props.parentTable && field.id !== id && isAutoPopulated(field))
        if (!siblingElements?.length) return;

        // A column that is itself a dropdown or a reference stores an option id / row
        // uuid rather than display text, so the raw cell has to be resolved the same
        // way the table resolves it before it can be compared with an option label.
        const masterFields = masterList?.find(master => master.dataId === props.parentTable)?.fields || []
        const globalLabelMap = buildGlobalMasterLabelMap(masterList, contentLang)
        const columnLabelMaps = new Map()
        const resolveColumnText = (column, rawValue) => {
            if (!columnLabelMaps.has(column)) {
                const columnField = masterFields.find(field => field?.label === column)
                columnLabelMaps.set(column, buildMasterOptionLabelMap(columnField, masterList, contentLang))
            }
            return resolveMasterValueLabel(rawValue, columnLabelMaps.get(column), globalLabelMap)
        }

        const sameText = (a, b) => (
            a !== undefined && a !== null && a !== "" && String(a).trim() === String(b).trim()
        )

        // Pick the option that stands for `rawValue`. Every option baked from this
        // master carries a row uuid as its value, so the canonical match is row to
        // row on the raw cell: that holds whatever the column stores — text, option
        // id or referenced row uuid — and needs no language. The text comparisons
        // are fallbacks for options not baked from this master, and for a value whose
        // own row was dropped when the option list was deduplicated by label.
        const findSiblingOption = (sibling, column, rawValue) => {
            const options = Array.isArray(sibling?.options) ? sibling.options : []
            if (!options.length) return undefined

            // A reference column answers with the id its cell holds, so the raw cell
            // is the option value itself.
            const byReferencedId = options.find(opt => sameText(opt?.value, rawValue))
            if (byReferencedId) return byReferencedId

            const byRow = options.find(opt => {
                const optionRow = optionsList.find(row => String(row?.uuid) === String(opt?.value))
                return optionRow && sameText(optionRow[column], rawValue)
            })
            if (byRow) return byRow

            const byRawText = options.find(opt => sameText(opt?.label, rawValue))
            if (byRawText) return byRawText

            const resolvedValue = resolveColumnText(column, rawValue)
            return options.find(opt => (
                sameText(opt?.label, resolvedValue) || sameText(resolveColumnText(column, opt?.label), resolvedValue)
            ))
        }

        // Dropdowns store an option id. Text / number siblings have no options, so
        // writing "" when the match fails wiped Address while City (a dropdown, or
        // a field that was not auto-filled) still posted.
        const isOptionSibling = (sibling) => {
            const formType = sibling?.formType
            if (formType === "dropdownForm" || formType === "dropdownField" || formType === "selectListForm"
                || formType === "radioForm" || formType === "radioField"
                || formType === "mcqForm" || formType === "multipleChoiceField" || formType === "multipleChoiceForm"
                || formType === "checkboxForm" || formType === "checkboxField") {
                return true
            }
            const inputType = sibling?.inputType
            if (inputType === "dropdown" || inputType === "select" || inputType === "radio"
                || inputType === "checkbox" || inputType === "mcq") {
                return true
            }
            return Array.isArray(sibling?.options) && sibling.options.length > 0
        }

        const textFromMasterCell = (column, rawValue) => {
            const resolved = resolveColumnText(column, rawValue)
            if (resolved !== undefined && resolved !== null && String(resolved).trim() !== "" && !isUuidLike(String(resolved))) {
                return resolved
            }
            if (rawValue !== undefined && rawValue !== null && String(rawValue).trim() !== "" && !isUuidLike(String(rawValue))) {
                return rawValue
            }
            return ""
        }

        if (selectedValue) {
            // Find the master data row by UUID (option values are row UUIDs), or by the
            // cell when the field is a reference column and answers with the id it holds
            const selectedRow = optionsList.find(row => row.uuid === selectedValue)
                || optionsList.find(row => String(row?.[canonicalColumn]) === String(selectedValue))
            if (selectedRow) {
                siblingElements.forEach(sibling => {
                    const column = sibling.mappedColumn || sibling.label
                    const rawValue = selectedRow[column]
                    if (rawValue === undefined || rawValue === null || rawValue === "") {
                        // Column has no value for this row — clear the field
                        form?.setFieldValue(sibling.id, "")
                        return
                    }
                    const matchingOption = findSiblingOption(sibling, column, rawValue)
                    if (matchingOption) {
                        form?.setFieldValue(sibling.id, matchingOption.value)
                    } else if (!isOptionSibling(sibling)) {
                        form?.setFieldValue(sibling.id, textFromMasterCell(column, rawValue))
                    } else {
                        form?.setFieldValue(sibling.id, "")
                    }
                })
            }
        } else {
            // Value cleared — clear all siblings
            siblingElements.forEach(sibling => {
                form?.setFieldValue(sibling.id, "")
            })
        }
    }, [formFields, id, props.parentTable, form, optionsList, canonicalColumn, enablePreviousDataFetch, isPreview, isEditing, fetchPreviousData, selfElement, masterList, contentLang])

    // Value to look for in the option list when the field is mapped to the
    // signed-in user's profile.
    const profileValue = useMemo(() => {
        if (!mapProfile || !profileField || !userInfo?.userId) return ""
        if (profileField === "name") return userInfo.name || ""
        if (profileField === "email") return userInfo.email || ""
        if (profileField === "employeeId") return userInfo.employeeId || userInfo.userId || ""
        return ""
    }, [mapProfile, profileField, userInfo])

    // Preselect the option matching the profile value. Only while the signed-in
    // user is authoring their own new/draft form — another viewer must not
    // inherit that mapping, and a submitted request keeps whatever was saved
    // (including a blank when the requester was not in master data).
    useEffect(() => {
        if (!profileValue || !isPreview || skipProfileAutofill || !requestContextReady || !filteredSelectOptions.length) return
        // On a master backed field the sibling fill needs the fetched rows. Hold
        // off until they arrive rather than preselecting now and leaving the rest
        // of the row blank — this effect re-runs once `optionsList` lands.
        if (props.parentTable && !optionsList?.length) return

        // An email mapped field can face options labelled either way round —
        // the raw address, or the person's name — so compare both forms on
        // each side.
        const targets = new Set([normalizeForMatch(profileValue)])
        if (String(profileValue).includes("@")) {
            targets.add(normalizeForMatch(getNameFromEmail(profileValue)))
            targets.add(normalizeForMatch(String(profileValue).split("@")[0]))
        }
        targets.delete("")

        const match = filteredSelectOptions.find((opt) => {
            const candidates = [opt.label, opt.value]
            if (typeof opt.label === "string" && opt.label.includes("@")) {
                candidates.push(getNameFromEmail(opt.label))
                candidates.push(opt.label.split("@")[0])
            }
            return candidates.some((candidate) => {
                const normalized = normalizeForMatch(candidate)
                return normalized !== "" && targets.has(normalized)
            })
        })
        if (!match) return

        // Delay slightly so any resetFields on mount has already run.
        const timer = setTimeout(() => {
            const current = form?.getFieldValue(id)
            const isEmpty = current === undefined || current === null || current === ""
                || (Array.isArray(current) && current.length === 0)
            if (!isEmpty) return
            form?.setFieldValue(id, multiSelect ? [match.value] : match.value)
            // Mirror a manual selection so master data siblings fill in too.
            handleDropChange(multiSelect ? [match.value] : match.value)
        }, 100)
        return () => clearTimeout(timer)
    }, [profileValue, isPreview, skipProfileAutofill, requestContextReady, filteredSelectOptions, optionsList, props.parentTable, form, id, multiSelect, handleDropChange])

    const selectedValues = useMemo(
        () => (Array.isArray(formValues?.[id]) ? formValues[id] : EMPTY_SELECTION),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [formValues?.[id], id]
    )
    const selectionLimit = enableScoring && multiSelect && maxSelectableOptions != null
        ? Number(maxSelectableOptions)
        : undefined
    const limitReached = selectionLimit != null && selectedValues.length >= selectionLimit

    // #7: Memoize final options passed to Select.
    // Labels are shown exactly as stored: an email column picked from a master must
    // render the address itself, not a name derived from its local part.
    const finalOptions = useMemo(() => {
        return displaySelectOptions.map(opt => {
            const disabledByLimit = limitReached && !selectedValues.includes(opt.value)
            return { ...opt, disabled: opt.disabled || disabledByLimit }
        })
    }, [displaySelectOptions, limitReached, selectedValues])

    const formLabel = (
        <>
            {label}
            {/* {selectedRowInfo && (
                <span
                    style={viewInfoStyle}
                    onClick={() => setInfoModalOpen(true)}
                >
                    <InfoCircleOutlined style={infoIconStyle} />View Info
                </span>
            )} */}
        </>
    )

    const handleSelectMouseDown = useCallback((e) => {
        const selectionItem = e.target.closest('.ant-select-selection-item');
        if (!selectionItem || !formValues?.[id] || !selectedRowInfo) {
            return;
        }

        suppressNextOpenRef.current = true;
        setDropdownOpen(false);
        e.preventDefault();
        e.stopPropagation();
        setInfoModalOpen(true);
    }, [formValues?.[id], id, selectedRowInfo]);

    const handleOpenChange = useCallback((nextOpen) => {
        if (suppressNextOpenRef.current) {
            suppressNextOpenRef.current = false;
            setDropdownOpen(false);
            return;
        }

        setDropdownOpen(nextOpen);
    }, []);

    // Dynamic modal styles based on space color
    const modalStyles = {
        content: {
            borderTop: spaceColor ? `4px solid ${spaceColor}` : "4px solid #ffc53d"
        },
        header: {
            borderBottom: spaceColor ? `1px solid ${spaceColor}` : "1px solid #ffc53d"
        }
    };

    return (
        <div className="form-element">
            {props.parentTable && form && masterSiblingIds.length > 0 && (
                <MasterSiblingWatcher
                    form={form}
                    siblingIds={masterSiblingIds}
                    onSlice={handleMasterSiblingSlice}
                />
            )}
            <Flex vertical>
                <Form.Item name={id} tooltip={help} rules={rules} className={selectedRowInfo ? "view-info-label" : ""}
                    label={<span
                    style={{
                        color: (highlight && !isPublicForm) ? "#722ed1" : "inherit",
                        fontWeight: (highlight && !isPublicForm) ? 600 : 400,
                        background: (highlight && !isPublicForm) ? "#f5eeff" : "transparent",
                        padding: "2px 6px",
                        borderRadius: 4,
                    }}
                >
                    {formLabel}
                </span>}
                >
                    <Select
                        mode={multiSelect ? "multiple" : undefined}
                        allowClear
                        placeholder={placeholder}
                        size="large"
                        style={selectStyle}
                        disabled={disabled}
                        options={finalOptions}
                        onChange={handleDropChange}
                        optionFilterProp="label"
                        showSearch
                        open={dropdownOpen}
                        onOpenChange={handleOpenChange}
                        onMouseDown={handleSelectMouseDown}
                    />
                </Form.Item>
            </Flex>

            <Modal
                open={infoModalOpen}
                title={`${label} Details`}
                onCancel={() => setInfoModalOpen(false)}
                footer={null}
                width={700}
                styles={{
                    content: modalStyles.content,
                    header: modalStyles.header,
                    body: { maxHeight: "60vh", overflowY: "auto" }
                }}
            >
                <div style={modalGridStyle}>
                    {selectedRowInfo && infoFields.map(({ key, value }) => (
                        <div key={key} style={modalFieldStyle}>
                            <label style={modalLabelStyle}>{key}</label>
                            <div style={{...modalValueStyle, borderColor: spaceColor || "#d9d9d9"}}>{String(value ?? "")}</div>
                        </div>
                    ))}
                </div>
            </Modal>
        </div>
    )
}

export default DropdownInput